import { useCallback, useMemo } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useLocalization } from "@/context/localization-context";
import { SectionCard } from "../../components/primitives";
import { TimeSeriesChart, type ChartSeries } from "../../components/time-series-chart";
import { formatCount } from "../../format";
import { SERIES } from "../../palette";
import type { BbSection, MetricSeries } from "../../types";
import { counterTotal, counterValues } from "../pipeline/helpers";
import { MetricChartCard, SeriesTotals } from "../pipeline/shared";
import { BB_KINDS, GROUP_COLORS, OUTCOME_GROUPS, outcomeGroup } from "./helpers";

/** Every BiteBerry webhook per bucket by kind (all branches), from the stored metrics. */
export function BbVolumeChart({ query }: { query: UseQueryResult<MetricSeries> }) {
  const { t } = useLocalization();
  const build = useCallback(
    (metrics: MetricSeries): ChartSeries[] =>
      BB_KINDS.map((kind, i) => ({
        key: `bb_${kind}`,
        label: t(`monitoring.bb.series.${kind}`),
        color: SERIES[i],
        values: counterValues(metrics, `bb.${kind}`),
      })),
    [t],
  );
  return (
    <MetricChartCard
      title={t("monitoring.bb.chart.volume.title")}
      description={t("monitoring.bb.chart.volume.description")}
      query={query}
      build={build}
      format={formatCount}
      integer
      height={220}
      footer={(metrics) => (
        <SeriesTotals
          items={BB_KINDS.map((kind) => ({
            label: t(`monitoring.bb.series.${kind}`),
            value: formatCount(counterTotal(metrics, `bb.${kind}`)),
          }))}
        />
      )}
    />
  );
}

/** Logged item webhooks of branches with a camera per bucket, stacked by what the backend did. */
export function BbOutcomeChart({ data }: { data: BbSection }) {
  const { t } = useLocalization();
  const { series, totals } = useMemo(() => {
    const grouped = Object.fromEntries(OUTCOME_GROUPS.map((g) => [g, data.t.map(() => 0)])) as Record<string, number[]>;
    const sums = Object.fromEntries(OUTCOME_GROUPS.map((g) => [g, 0])) as Record<string, number>;
    for (const [key, values] of Object.entries(data.series)) {
      if (!key.startsWith("item.")) continue;
      const group = outcomeGroup(key.slice("item.".length));
      values.forEach((v, i) => {
        grouped[group][i] += v ?? 0;
        sums[group] += v ?? 0;
      });
    }
    return {
      series: OUTCOME_GROUPS.map<ChartSeries>((group) => ({
        key: `group_${group}`,
        label: t(`monitoring.bb.group.${group}`),
        color: GROUP_COLORS[group],
        type: "bar",
        stackId: "outcome",
        values: grouped[group],
      })),
      totals: sums,
    };
  }, [data, t]);

  return (
    <SectionCard title={t("monitoring.bb.chart.outcomes.title")} description={t("monitoring.bb.chart.outcomes.description")}>
      <div className="flex flex-col gap-3">
        <TimeSeriesChart
          t={data.t}
          stepMs={data.step_ms}
          window={data.window}
          series={series}
          height={220}
          format={formatCount}
          integer
          emptyText={t("monitoring.noData")}
        />
        <SeriesTotals
          items={OUTCOME_GROUPS.map((group) => ({
            label: t(`monitoring.bb.group.${group}`),
            value: formatCount(totals[group]),
            tone: (group === "no_order" || group === "failed") && totals[group] > 0 ? "nok" : "neutral",
          }))}
        />
      </div>
    </SectionCard>
  );
}
